<template>
  <div v-click-outside="handleClickOutside" class="search">
    <SearchIcon
      v-if="$device.isMobile"
      class="js-prevent-handle-outside search__toggle"
      width="23"
      height="23"
      @click="toggle"
    />
    <div v-if="!$device.isMobile" class="search__field-wrapper">
      <input
        v-model="query"
        type="text"
        :name="queryId"
        placeholder="Search Bravado"
        class="search__field-input"
        @keyup.enter="simulateClick()"
        @keyup.down="focusItem(1)"
        @keyup.up="focusItem(-1)"
        @focus="handleFieldFocus"
      />
      <SearchIcon v-if="!loading" width="18" height="18" class="search__icon" />
      <DualRingLoader :visible="loading" :size="18" color="#797b86" class="search__icon search__icon--loader" />
    </div>
    <div
      v-if="resultsVisible"
      v-follow-selection="{ index: selectedIndex, selector: '.search__item--selected' }"
      class="search__results"
    >
      <div v-if="$device.isMobile" class="search__field">
        <input
          v-model="query"
          type="text"
          :name="queryId"
          placeholder="Search Bravado"
          class="search__input"
          @focus="handleFieldFocus"
        />
        <SearchIcon v-if="!loading" class="search__icon search__icon--mobile" />
        <DualRingLoader :visible="loading" :size="18" color="#FFFFFF" class="search__loader" />
      </div>
      <SearchPlaceholder v-if="hasNoResults" class="search__placeholder" />
      <template v-if="hasUsers">
        <div id="users"></div>
        <div v-scroll-to="'#users'" class="search__ruler">
          <div class="search__text">Members</div>
        </div>
        <SearchUser
          v-for="(user, index) in users"
          :key="'uk_' + index"
          :class="{ 'search__item--selected': isSelected(index, 'users') }"
          :user="user"
          class="search__item"
          @click="handleItemClick(user, 'users')"
        />
        <div v-show="list.users.hasMore" class="search__action">
          <button class="search__more" @click="handleMore('users')">Show More</button>
        </div>
      </template>
      <template v-if="hasContent">
        <div id="content"></div>
        <!-- <div v-scroll-to="'#content'" class="search__ruler search__ruler--sticky">
          <div class="search__text">Content</div>
        </div> -->
        <SearchContent
          v-for="(c, index) in content"
          :key="'conk_' + index"
          :class="{ 'search__item--selected': isSelected(index, 'content') }"
          :content="c"
          class="search__item"
          @click="handleItemClick(c, 'content')"
        />
        <div v-show="list.content.hasMore" class="search__action">
          <button class="search__more" @click="handleMore('content')">Show More</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import SearchPlaceholder from './SearchPlaceholder.vue';
import SearchUser from './SearchUser.vue';
import SearchContent from './SearchContent.vue';
import followSelection from './followSelection';
import { prepareData } from './utils';
import scrollTo from '~/directives/scrollTo.js';
import SearchIcon from '~/assets/svg/menu-search.vue.svg';
import DualRingLoader from '~/components/common/DualRingLoader.vue';
import debounce from '~/components/utils/debounce';
import clickOutside from '~/directives/click_outside';
import { togglePageLock } from '~/components/utils/browser/ui';
import { guid } from '~/components/utils/core/uid';
import { redirectTo } from '~/components/utils/browser/url';

const PER_PAGE = 5;

const transitions = {
  none: 'users',
  users: 'communities',
  communities: 'content',
  content: 'none',
};
const backTransitions = Object.keys(transitions).reduce(function revertTransitions(acc, key) {
  return { ...acc, [transitions[key]]: key };
}, {});

export default {
  name: 'Search',
  components: {
    SearchIcon,
    DualRingLoader,
    SearchPlaceholder,
    SearchUser,
    SearchContent,
  },
  directives: {
    'click-outside': clickOutside,
    'scroll-to': scrollTo,
    'follow-selection': followSelection,
  },
  data() {
    return {
      selectedIndex: -1,
      selectedSection: 'users',
      list: {
        users: { data: [], page: 0, total: 0, hasMore: false },
        content: { data: [], page: 0, total: 0, hasMore: false },
        communities: { data: [], page: 0, total: 0, hasMore: false },
      },
      resultsVisible: false,
      query: '',
      queryId: guid(),
      called: false,
      loading: false,
    };
  },
  computed: {
    currentSectionSize() {
      if (this.selectedSection === 'none') {
        return 0;
      }
      return this[this.selectedSection].length;
    },
    users() {
      const {
        users: { data, page },
      } = this.list;
      return data.slice(0, (page + 1) * PER_PAGE);
    },
    hasUsers() {
      return this.list.users.data.length;
    },
    communities() {
      const {
        communities: { data, page },
      } = this.list;
      return data.slice(0, (page + 1) * PER_PAGE);
    },
    content() {
      const {
        content: { data, page },
      } = this.list;
      return data.slice(0, (page + 1) * PER_PAGE);
    },
    hasContent() {
      return this.list.content.data.length;
    },
    hasNoResults() {
      return !this.hasUsers && !this.hasContent && this.called && !this.loading;
    },
    communitiesStyle() {
      const offset = this.hasContent ? 28 : -6;
      return {
        bottom: `${offset}px`,
      };
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    query: debounce(function (q) {
      const value = q.trim();

      if (!value) return;

      this.queryId = guid();
      this.loading = true;

      this.$analytics.logEvent('search__query__type', { query: q, queryId: this.queryId });

      let id;
      let hash;

      if (this.$auth.loggedIn) {
        id = this.$auth.user.id;
        hash = this.$auth.user.searchToken;
      }

      this.$api
        .$get(process.env.search.endpoint, { params: { query: value, user_id: id, hash } })
        .then(data => {
          this.list = prepareData(data, PER_PAGE);
          this.resultsVisible = true;
          this.called = true;

          this.$analytics.logEvent('search__results__loaded', {
            query: q,
            queryId: this.queryId,
            users_count: this.list.users.data.length,
            content_count: this.list.content.data.length,
            communities_count: this.list.communities.data.length,
          });
        })
        .catch(e => {
          this.$notice.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    }, 500),
    resultsVisible(newValue) {
      if (this.$device.isMobile) {
        togglePageLock(newValue);
      }
    },
  },
  methods: {
    toggle() {
      this.resultsVisible = !this.resultsVisible;
    },
    simulateClick() {
      const item = this[this.selectedSection][this.selectedIndex];

      if (item) {
        this.handleItemClick(item, this.selectedSection, true);
      }
    },
    isSelected(index, section) {
      return this.selectedSection === section && index === this.selectedIndex;
    },
    focusItem(step) {
      this.selectedIndex = this.selectedIndex + step;

      if (this.selectedSection === 'none' && step === 0) {
        return;
      }

      if (this.selectedIndex + 1 > this.currentSectionSize) {
        this.selectedSection = transitions[this.selectedSection];
        this.selectedIndex = 0;
        this.focusItem(0);
      }

      if (this.selectedIndex < 0) {
        this.selectedSection = backTransitions[this.selectedSection];
        this.selectedIndex = this.currentSectionSize - 1;
        this.focusItem(0);
      }
    },
    handleClickOutside() {
      this.resultsVisible = false;
    },
    handleFieldFocus() {
      if (this.query) {
        this.resultsVisible = true;
      }
    },
    handleMore(field) {
      const page = this.list[field].page + 1;
      this.list[field].page = page;
      this.list[field].hasMore = page + 1 < this.list[field].total;

      this.$analytics.logEvent('search__more__click', {
        query: this.query,
        queryId: this.queryId,
        section: field,
        page: page + 1,
        total: this.list[field].total,
      });
    },
    handleItemClick(item, section, shouldRedirect = false) {
      let data = {};

      if (section === 'users') {
        data = {
          id: item.user_id,
          slug: item.slug,
        };
      } else if (section === 'communities') {
        data = {
          id: item.community_id,
          slug: item.name,
        };
      } else if (section === 'content') {
        data = {
          id: item.content_id,
          slug: item.post_slug,
        };
      }

      this.$analytics.logEvent('search__results__click', {
        query: this.query,
        queryId: this.queryId,
        section,
        data,
      });

      this.resultsVisible = false;
      this.query = '';

      if (shouldRedirect) redirectTo(item.link);
    },
  },
};
</script>

<style scoped lang="scss">
$top-mobile: 48px;
$field-background: var(--universal-color-black);
$field-border-color: var(--universal-color-gray-80);
$field-color: var(--universal-color-white);
$field-font: var(--font-family);
$icon-color: var(--universal-color-white);
$results-background: var(--universal-color-black);
$results-selected-background: var(--universal-color-gray-80);
$results-border-color: var(--universal-color-gray-80);
$results-color: var(--universal-color-white);
$ruler-background: var(--universal-color-black);
$ruler-divider-background: var(--universal-color-gray-80);
$text-background: var(--universal-color-black);
$button-background: var(--universal-color-gray-90);
$button-border-color: var(--universal-color-gray-80);
$button-color: var(--universal-color-gray-20);
$button-hover-background: var(--universal-color-gray-70);
$button-hover-color: var(--universal-color-white);
$placeholder-color: rgba(var(--universal-color-white-rgb), 0.6);

.search {
  @include placeholder-color($placeholder-color);

  $this: &;

  position: relative;
  display: flex;

  @include mq('sm') {
    display: block;
  }

  &__toggle {
    color: var(--theme-color-text);

    @include mq('sm') {
      display: none;
    }
  }

  &__icon {
    position: absolute;
    left: 10px;
    top: 50%;
    margin-top: -9px;
    color: var(--universal-color-gray-50);

    &--loader {
      left: 18px;
      margin-top: -1px;
    }

    &--mobile {
      top: 10px;
      right: 16px;
      left: auto;
      color: var(--universal-color-gray-50);
      width: 18px;
      height: 18px;
      margin-top: 0;
    }
  }

  &__field-wrapper {
    position: relative;
    color: var(--universal-color-gray-50);
    display: none;

    @include mq('sm') {
      display: block;
    }
  }

  &__field-input {
    @include body;

    width: 100%;
    background: var(--theme-color-over-card);
    border: 1px solid transparent;
    border-radius: 6px;
    padding: 5px 10px 5px 34px;
    appearance: none;
    outline: none;
    color: var(--theme-color-text);
    transition: background-color ease-in-out 300ms;

    &:focus {
      border: 1px solid var(--universal-color-brand);
    }

    &::placeholder {
      color: var(--universal-color-gray-50);
    }
  }

  &__field {
    display: block;
    width: 100%;
    border-top: 1px solid $field-border-color;
    border-bottom: 1px solid $field-border-color;
    background: var(--theme-color-over-card);
    position: relative;
    color: var(--theme-color-text);

    &::placeholder {
      color: var(--universal-color-gray-50);
    }

    @include mq('sm') {
      border: 1px solid $field-border-color;
      border-radius: 4px;
    }

    &--builtin {
      display: none;

      @include mq('sm') {
        display: block;
      }
    }
  }

  &__input {
    appearance: none;
    padding: 8px 57px 7px 17px;
    background: transparent;
    color: var(--theme-color-text);
    font-family: $field-font;
    //fixme: no such typography mixin

    font-size: 16px;
    line-height: 1.43;
    outline: none;
    border: none;
    width: 100%;

    @include mq('sm') {
      @include body;
    }

    &::placeholder {
      color: var(--universal-color-gray-50);
    }
  }

  &__loader {
    position: absolute;
    top: 8px;
    right: 16px;
    width: 18px;
    height: 18px;

    @include mq('sm') {
      width: 24px;
      height: 24px;
    }
  }

  &__results {
    position: fixed;
    top: calc(32px + 16px);
    left: 0;
    right: 0;
    bottom: 72px;
    background: var(--theme-color-card);
    color: $results-color;
    overflow-y: auto;
    padding: 0;
    min-width: 320px;
    z-index: 1002;
    min-height: 168px;

    @include mq('sm') {
      position: absolute;
      top: 100%;
      margin-top: 4px;
      max-height: 600px;
      bottom: auto;
      border: 1px solid var(--theme-color-border);
      border-radius: 6px;
    }
  }

  &__ruler {
    padding: 8px 0 7px;
    text-align: center;
    position: relative;
    background: var(--theme-color-card);

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 1px;
      transform: translateY(-50%);
      background: $ruler-divider-background;
    }

    &--sticky {
      position: sticky;
      bottom: -6px;
      left: 0;
      right: 0;
    }
  }

  &__text {
    position: relative;
    display: inline-block;
    padding: 0 20px;
    // fixme: no such typography mixin

    font-weight: 500;
    font-size: 10px;
    line-height: 1.2;
    text-transform: uppercase;
    background: var(--theme-color-card);
    cursor: pointer;
  }

  &__item {
    padding: 16px;
    border-bottom: 1px solid var(--theme-color-border);

    &:hover,
    &--selected {
      background: var(--theme-color-over-card);
    }
  }

  &__more {
    @include body-semibold;

    width: 100%;
    cursor: pointer;
    border-radius: 6px;
    text-align: center;
    color: var(--universal-color-gray-50);
    padding: 14px 12px;
    appearance: none;
    outline: none;
    background: transparent;
    border: none;
    transition: color 0.3s;

    &:hover {
      color: var(--universal-color-gray-50-light);
    }
  }

  &__placeholder {
    padding: 16px;
  }

  // stylelint-disable no-duplicate-selectors
  & {
    #{$this}__loader {
      // to avoid important

      position: absolute;
    }
  }
}
</style>
